/**
 * FinList Category System - SINGLE SOURCE OF TRUTH
 * =================================================
 * 
 * This file consolidates ALL category definitions for the entire FinList application.
 * Previously split between categoryTaxonomy.js, unifiedCategories.js, envelopeCategories.js,
 * and categoryCore.js - now merged into ONE authoritative source.
 * 
 * ARCHITECTURE:
 * - EXPENSE_CATEGORIES: 12 main expense categories with subcategories
 * - INCOME_CATEGORIES: 12 income source categories
 * - SAVINGS_CATEGORIES: 8 savings goal categories  
 * - BUDGET_ENVELOPES: Pre-configured envelope budgets
 * - SHOPPING_LIST_CATEGORIES: Shopping-specific categories
 * - LEGACY_ID_MAP: Backward compatibility mappings
 * 
 * USAGE:
 * import { 
 *   EXPENSE_CATEGORIES, 
 *   INCOME_CATEGORIES,
 *   findCategoryById,
 *   getCategoryDisplayName,
 *   normalizeCategoryId
 * } from '@/constants/categorySystem';
 * 
 * @version 3.0.0
 * @author FinList Team
 */

import { 
  ShoppingCart, Zap, Home, Car, User, Heart, BookOpen, 
  Film, PiggyBank, TrendingUp, Gift, Smartphone, Shield,
  Plane, CreditCard, Briefcase, MoreHorizontal,
  DollarSign, Laptop, Award, RefreshCw, Building, Banknote,
  GraduationCap, Baby, Target, Palmtree, Coffee, Shirt,
  Wallet, Globe, Clock, Package
} from 'lucide-react';

// ============================================================================
// CATEGORY TYPES
// ============================================================================

export const CATEGORY_TYPES = {
  EXPENSE: 'expense',
  INCOME: 'income',
  SAVINGS: 'savings'
};

// ============================================================================
// EXPENSE CATEGORIES (12 Main Categories)
// ============================================================================

export const EXPENSE_CATEGORIES = [
  {
    id: 'groceries-food',
    name: 'Groceries & Food',
    icon: ShoppingCart,
    emoji: '🛒',
    color: '#10b981',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'Food and grocery shopping',
    isEssential: true,
    defaultBudget: 400,
    budgetEnvelope: 'env_groceries',
    // FINLIST CONTEXT: Available in both expense entry and shopping list
    availableIn: ['expense', 'shopping'],
    subcategories: [
      { id: 'fresh-produce', name: 'Fresh Produce' },
      { id: 'protein-meat', name: 'Protein & Meat' },
      { id: 'pantry-staples', name: 'Pantry Staples' },
      { id: 'dairy-eggs', name: 'Dairy & Eggs' },
      { id: 'snacks-treats', name: 'Snacks & Treats' },
      { id: 'drinks-beverages', name: 'Drinks & Beverages' },
      { id: 'baby-food', name: 'Baby Food' },
      { id: 'pet-food', name: 'Pet Food' }
    ],
    // Legacy IDs that map to this category
    legacyIds: ['groceries', 'cat_groceries', 'food', 'grocery']
  },
  {
    id: 'utilities-bills',
    name: 'Utilities & Bills',
    icon: Zap,
    emoji: '⚡',
    color: '#f59e0b',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'Fixed monthly/biweekly bills',
    isEssential: true,
    isRecurring: true,
    defaultBudget: 200,
    budgetEnvelope: 'env_utilities',
    subcategories: [
      { id: 'electricity', name: 'Electricity' },
      { id: 'water', name: 'Water' },
      { id: 'gas-heating', name: 'Gas / Heating' },
      { id: 'internet', name: 'Internet' },
      { id: 'mobile-data', name: 'Mobile Data & Airtime' },
      { id: 'cable-tv', name: 'Cable/TV Subscription' },
      { id: 'waste-disposal', name: 'Waste / Disposal' }
    ],
    legacyIds: ['utilities', 'cat_utilities', 'bills']
  },
  {
    id: 'housing-rent',
    name: 'Housing & Rent',
    icon: Home,
    emoji: '🏡',
    color: '#0ea5e9',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'Housing and upkeep costs',
    isEssential: true,
    isRecurring: true,
    defaultBudget: 800,
    budgetEnvelope: 'env_housing',
    subcategories: [
      { id: 'rent', name: 'Rent' },
      { id: 'mortgage', name: 'Mortgage' },
      { id: 'repairs', name: 'Repairs & Maintenance' },
      { id: 'home-insurance', name: 'Home Insurance' },
      { id: 'furniture', name: 'Furniture' },
      { id: 'home-decor', name: 'Home Décor' },
      { id: 'cleaning-supplies', name: 'Cleaning Supplies' },
      { id: 'contractors', name: 'Contractors / Labour' }
    ],
    legacyIds: ['housing-costs', 'cat_housing', 'housing', 'rent', 'mortgage', 'household-purchases']
  },
  {
    id: 'transportation',
    name: 'Transportation',
    icon: Car,
    emoji: '🚗',
    color: '#8b5cf6',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'All mobility-related costs',
    isEssential: true,
    defaultBudget: 150,
    budgetEnvelope: 'env_transport',
    subcategories: [
      { id: 'fuel-charging', name: 'Fuel / Charging' },
      { id: 'public-transport', name: 'Public Transport' },
      { id: 'ride-hailing', name: 'Ride Hailing (Uber, Bolt)' },
      { id: 'vehicle-maintenance', name: 'Vehicle Maintenance' },
      { id: 'parking', name: 'Parking' },
      { id: 'car-insurance', name: 'Car Insurance' },
      { id: 'vehicle-tax', name: 'Vehicle Tax' }
    ],
    legacyIds: ['cat_transportation', 'transport', 'car', 'auto']
  },
  {
    id: 'personal-care',
    name: 'Personal Care',
    icon: User,
    emoji: '👤',
    color: '#ec4899',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'Personal grooming and health upkeep',
    isEssential: false,
    defaultBudget: 100,
    budgetEnvelope: 'env_personal',
    // FINLIST CONTEXT: Available in both expense entry and shopping list
    availableIn: ['expense', 'shopping'],
    subcategories: [
      { id: 'toiletries', name: 'Toiletries' },
      { id: 'haircuts-salon', name: 'Haircuts / Salon' },
      { id: 'skincare', name: 'Skincare' },
      { id: 'cosmetics', name: 'Cosmetics' },
      { id: 'gym-fitness', name: 'Gym / Fitness' },
      { id: 'spa-wellness', name: 'Spa / Wellness' }
    ],
    legacyIds: ['cat_personal_care', 'grooming', 'hygiene']
  },
  {
    id: 'health-medical',
    name: 'Health & Medical',
    icon: Heart,
    emoji: '❤️',
    color: '#ef4444',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'Medical and wellness-related costs',
    isEssential: true,
    defaultBudget: 150,
    budgetEnvelope: 'env_health',
    subcategories: [
      { id: 'medications', name: 'Medications' },
      { id: 'doctor-visits', name: 'Doctor Visits' },
      { id: 'health-insurance', name: 'Health Insurance' },
      { id: 'dental-care', name: 'Dental Care' },
      { id: 'eye-care', name: 'Eye Care' },
      { id: 'supplements', name: 'Supplements' },
      { id: 'first-aid', name: 'First Aid' }
    ],
    legacyIds: ['cat_healthcare', 'healthcare', 'medical']
  },
  {
    id: 'education-learning',
    name: 'Education & Learning',
    icon: BookOpen,
    emoji: '📚',
    color: '#06b6d4',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'Education and learning expenses',
    isEssential: true,
    defaultBudget: 200,
    budgetEnvelope: 'env_education',
    subcategories: [
      { id: 'tuition-fees', name: 'Tuition Fees' },
      { id: 'books-materials', name: 'Books / Materials' },
      { id: 'online-courses', name: 'Online Courses' },
      { id: 'school-supplies', name: 'School Supplies' },
      { id: 'exams', name: 'Exams' },
      { id: 'personal-development', name: 'Personal Development' }
    ],
    legacyIds: ['education', 'cat_education', 'school', 'learning', 'courses']
  },
  {
    id: 'subscriptions',
    name: 'Subscriptions',
    icon: CreditCard,
    emoji: '📱',
    color: '#6366f1',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'Recurring subscriptions',
    isEssential: false,
    isRecurring: true,
    defaultBudget: 100,
    budgetEnvelope: 'env_subscriptions',
    subcategories: [
      { id: 'streaming-services', name: 'Streaming Services' },
      { id: 'apps-software', name: 'Apps / Software' },
      { id: 'gym-membership', name: 'Gym Membership' },
      { id: 'professional-tools', name: 'Professional Tools' },
      { id: 'news-magazines', name: 'News / Magazines' }
    ],
    legacyIds: ['cat_subscriptions', 'recurring']
  },
  {
    id: 'insurance',
    name: 'Insurance',
    icon: Shield,
    emoji: '🛡️',
    color: '#84cc16',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'Insurance premiums',
    isEssential: true,
    isRecurring: true,
    defaultBudget: 200,
    budgetEnvelope: 'env_insurance',
    subcategories: [
      { id: 'health-insurance', name: 'Health Insurance' },
      { id: 'life-insurance', name: 'Life Insurance' },
      { id: 'home-insurance', name: 'Home Insurance' },
      { id: 'car-insurance', name: 'Car Insurance' }
    ],
    legacyIds: ['cat_insurance']
  },
  {
    id: 'gifts-donations',
    name: 'Gifts & Donations',
    icon: Gift,
    emoji: '🎁',
    color: '#f43f5e',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'Gifts and charitable giving',
    isEssential: false,
    defaultBudget: 100,
    budgetEnvelope: 'env_gifts',
    subcategories: [
      { id: 'birthdays', name: 'Birthdays' },
      { id: 'family-support', name: 'Family Support' },
      { id: 'charity', name: 'Charity' },
      { id: 'religious-giving', name: 'Religious Giving' },
      { id: 'seasonal-gifts', name: 'Seasonal Gifts' }
    ],
    legacyIds: ['gifts', 'donations', 'charity']
  },
  {
    id: 'leisure-lifestyle',
    name: 'Leisure & Lifestyle',
    icon: Coffee,
    emoji: '☕',
    color: '#a855f7',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'Entertainment and lifestyle',
    isEssential: false,
    defaultBudget: 200,
    budgetEnvelope: 'env_lifestyle',
    subcategories: [
      { id: 'dining-out', name: 'Dining Out' },
      { id: 'entertainment', name: 'Entertainment' },
      { id: 'travel', name: 'Travel' },
      { id: 'hobbies', name: 'Hobbies' },
      { id: 'clothing', name: 'Clothing' },
      { id: 'electronics-gadgets', name: 'Electronics / Gadgets' },
      { id: 'shopping', name: 'Shopping' }
    ],
    legacyIds: ['cat_entertainment', 'cat_shopping', 'cat_travel', 'cat_dining', 'entertainment', 'shopping', 'travel', 'dining']
  },
  {
    id: 'other-expenses',
    name: 'Other Expenses',
    icon: MoreHorizontal,
    emoji: '📦',
    color: '#6b7280',
    type: CATEGORY_TYPES.EXPENSE,
    description: 'Miscellaneous expenses',
    isEssential: false,
    defaultBudget: 100,
    budgetEnvelope: 'env_other',
    subcategories: [
      { id: 'miscellaneous', name: 'Miscellaneous' },
      { id: 'fees-charges', name: 'Fees & Charges' },
      { id: 'unexpected', name: 'Unexpected' }
    ],
    legacyIds: ['other', 'misc', 'uncategorized']
  }
];

// ============================================================================
// INCOME CATEGORIES (12 Categories)
// ============================================================================

export const INCOME_CATEGORIES = [
  {
    id: 'salary-wages',
    name: 'Salary & Wages',
    icon: Briefcase,
    emoji: '💼',
    color: '#10b981',
    type: CATEGORY_TYPES.INCOME,
    description: 'Regular employment income',
    isRecurring: true,
    subcategories: [
      { id: 'base-salary', name: 'Base Salary' },
      { id: 'overtime', name: 'Overtime' },
      { id: 'bonuses', name: 'Bonuses' },
      { id: 'commissions', name: 'Commissions' }
    ],
    legacyIds: ['cat_salary', 'salary', 'wages', 'paycheck']
  },
  {
    id: 'freelance-business',
    name: 'Freelance & Business',
    icon: Laptop,
    emoji: '💻',
    color: '#3b82f6',
    type: CATEGORY_TYPES.INCOME,
    description: 'Self-employment income',
    subcategories: [
      { id: 'freelance-work', name: 'Freelance Work' },
      { id: 'consulting', name: 'Consulting' },
      { id: 'business-income', name: 'Business Income' },
      { id: 'side-hustles', name: 'Side Hustles' }
    ],
    legacyIds: ['cat_freelance', 'freelance', 'business', 'self-employed']
  },
  {
    id: 'investments-returns',
    name: 'Investments & Returns',
    icon: TrendingUp,
    emoji: '📈',
    color: '#8b5cf6',
    type: CATEGORY_TYPES.INCOME,
    description: 'Investment returns',
    subcategories: [
      { id: 'dividends', name: 'Dividends' },
      { id: 'interest', name: 'Interest' },
      { id: 'capital-gains', name: 'Capital Gains' },
      { id: 'crypto-gains', name: 'Crypto Gains' }
    ],
    legacyIds: ['cat_investments', 'investments', 'dividends', 'returns']
  },
  {
    id: 'rental-income',
    name: 'Rental Income',
    icon: Building,
    emoji: '🏢',
    color: '#0ea5e9',
    type: CATEGORY_TYPES.INCOME,
    description: 'Property rental income',
    isRecurring: true,
    subcategories: [
      { id: 'property-rent', name: 'Property Rent' },
      { id: 'airbnb', name: 'Airbnb / Short-term' },
      { id: 'parking-space', name: 'Parking Space' }
    ],
    legacyIds: ['cat_rental', 'rental', 'rent-income']
  },
  {
    id: 'benefits-allowances',
    name: 'Benefits & Allowances',
    icon: Award,
    emoji: '🏆',
    color: '#f59e0b',
    type: CATEGORY_TYPES.INCOME,
    description: 'Work benefits and allowances',
    subcategories: [
      { id: 'housing-allowance', name: 'Housing Allowance' },
      { id: 'transport-allowance', name: 'Transport Allowance' },
      { id: 'meal-allowance', name: 'Meal Allowance' },
      { id: 'pension-contribution', name: 'Pension Contribution' }
    ],
    legacyIds: ['cat_benefits', 'benefits', 'allowances']
  },
  {
    id: 'gifts-received',
    name: 'Gifts Received',
    icon: Gift,
    emoji: '🎁',
    color: '#ec4899',
    type: CATEGORY_TYPES.INCOME,
    description: 'Monetary gifts received',
    subcategories: [
      { id: 'family-gifts', name: 'Family Gifts' },
      { id: 'birthday-gifts', name: 'Birthday Gifts' },
      { id: 'wedding-gifts', name: 'Wedding Gifts' }
    ],
    legacyIds: ['cat_gifts_received', 'gifts-income']
  },
  {
    id: 'government-benefits',
    name: 'Government Benefits',
    icon: Banknote,
    emoji: '🏛️',
    color: '#64748b',
    type: CATEGORY_TYPES.INCOME,
    description: 'Government assistance',
    subcategories: [
      { id: 'tax-refunds', name: 'Tax Refunds' },
      { id: 'unemployment', name: 'Unemployment Benefits' },
      { id: 'child-benefits', name: 'Child Benefits' },
      { id: 'disability', name: 'Disability Benefits' }
    ],
    legacyIds: ['cat_government', 'government', 'benefits']
  },
  {
    id: 'refunds-reimbursements',
    name: 'Refunds & Reimbursements',
    icon: RefreshCw,
    emoji: '🔄',
    color: '#22c55e',
    type: CATEGORY_TYPES.INCOME,
    description: 'Money returned or reimbursed',
    subcategories: [
      { id: 'product-refunds', name: 'Product Refunds' },
      { id: 'expense-reimbursements', name: 'Expense Reimbursements' },
      { id: 'insurance-claims', name: 'Insurance Claims' }
    ],
    legacyIds: ['cat_refunds', 'refunds', 'reimbursements']
  },
  {
    id: 'pension-retirement',
    name: 'Pension & Retirement',
    icon: Wallet,
    emoji: '👴',
    color: '#6366f1',
    type: CATEGORY_TYPES.INCOME,
    description: 'Retirement income',
    isRecurring: true,
    subcategories: [
      { id: 'pension-payments', name: 'Pension Payments' },
      { id: 'retirement-fund', name: 'Retirement Fund Withdrawals' }
    ],
    legacyIds: ['cat_pension', 'pension', 'retirement']
  },
  {
    id: 'grants-scholarships',
    name: 'Grants & Scholarships',
    icon: GraduationCap,
    emoji: '🎓',
    color: '#06b6d4',
    type: CATEGORY_TYPES.INCOME,
    description: 'Educational funding',
    subcategories: [
      { id: 'scholarships', name: 'Scholarships' },
      { id: 'research-grants', name: 'Research Grants' },
      { id: 'educational-grants', name: 'Educational Grants' }
    ],
    legacyIds: ['cat_grants', 'grants', 'scholarships']
  },
  {
    id: 'sale-of-assets',
    name: 'Sale of Assets',
    icon: Package,
    emoji: '📦',
    color: '#f97316',
    type: CATEGORY_TYPES.INCOME,
    description: 'Income from selling items',
    subcategories: [
      { id: 'property-sale', name: 'Property Sale' },
      { id: 'vehicle-sale', name: 'Vehicle Sale' },
      { id: 'personal-items', name: 'Personal Items' }
    ],
    legacyIds: ['cat_sales', 'sales', 'assets']
  },
  {
    id: 'other-income',
    name: 'Other Income',
    icon: MoreHorizontal,
    emoji: '➕',
    color: '#6b7280',
    type: CATEGORY_TYPES.INCOME,
    description: 'Miscellaneous income',
    subcategories: [
      { id: 'miscellaneous-income', name: 'Miscellaneous' },
      { id: 'winnings', name: 'Winnings / Prizes' }
    ],
    legacyIds: ['cat_other_income', 'other-income', 'misc-income']
  }
];

// ============================================================================
// SAVINGS CATEGORIES (8 Categories)
// ============================================================================

export const SAVINGS_CATEGORIES = [
  {
    id: 'emergency-fund',
    name: 'Emergency Fund',
    icon: Shield,
    emoji: '🛡️',
    color: '#ef4444',
    type: CATEGORY_TYPES.SAVINGS,
    description: '3-6 months of expenses',
    targetMultiplier: 6, // 6x monthly expenses
    priority: 1,
    legacyIds: ['emergency', 'rainy-day']
  },
  {
    id: 'retirement-savings',
    name: 'Retirement',
    icon: Palmtree,
    emoji: '🏝️',
    color: '#10b981',
    type: CATEGORY_TYPES.SAVINGS,
    description: 'Long-term retirement savings',
    priority: 2,
    legacyIds: ['retirement', 'pension-savings']
  },
  {
    id: 'home-savings',
    name: 'Home Purchase',
    icon: Home,
    emoji: '🏠',
    color: '#3b82f6',
    type: CATEGORY_TYPES.SAVINGS,
    description: 'Saving for a home',
    priority: 3,
    legacyIds: ['home', 'house', 'property']
  },
  {
    id: 'education-savings',
    name: 'Education Fund',
    icon: GraduationCap,
    emoji: '🎓',
    color: '#8b5cf6',
    type: CATEGORY_TYPES.SAVINGS,
    description: 'Education savings (self or children)',
    priority: 4,
    legacyIds: ['education-fund', 'college', 'kids-education']
  },
  {
    id: 'vacation-travel',
    name: 'Vacation & Travel',
    icon: Plane,
    emoji: '✈️',
    color: '#f59e0b',
    type: CATEGORY_TYPES.SAVINGS,
    description: 'Travel and vacation fund',
    priority: 5,
    legacyIds: ['vacation', 'travel-savings', 'holiday']
  },
  {
    id: 'vehicle-savings',
    name: 'Vehicle Fund',
    icon: Car,
    emoji: '🚗',
    color: '#06b6d4',
    type: CATEGORY_TYPES.SAVINGS,
    description: 'Saving for a vehicle',
    priority: 6,
    legacyIds: ['car', 'vehicle', 'auto-savings']
  },
  {
    id: 'investment-capital',
    name: 'Investment Capital',
    icon: TrendingUp,
    emoji: '📈',
    color: '#22c55e',
    type: CATEGORY_TYPES.SAVINGS,
    description: 'Capital for investments',
    priority: 7,
    legacyIds: ['investments', 'capital', 'stocks']
  },
  {
    id: 'debt-paydown',
    name: 'Debt Paydown',
    icon: TrendingUp,
    emoji: '📉',
    color: '#dc2626',
    type: CATEGORY_TYPES.SAVINGS,
    description: 'Track repayments & momentum',
    priority: 8,
    legacyIds: ['debt', 'debt-repayment', 'loan-payoff', 'credit-card-payoff']
  },
  {
    id: 'other-savings',
    name: 'Other Goals',
    icon: Target,
    emoji: '🎯',
    color: '#ec4899',
    type: CATEGORY_TYPES.SAVINGS,
    description: 'Custom savings goals',
    priority: 9,
    legacyIds: ['other', 'custom', 'general-savings']
  }
];

// ============================================================================
// BUDGET ENVELOPES (Pre-configured budgets)
// ============================================================================

export const BUDGET_ENVELOPES = [
  { id: 'env_groceries', name: 'Groceries & Food', linkedCategory: 'groceries-food', defaultAmount: 400, color: '#10b981' },
  { id: 'env_utilities', name: 'Utilities & Bills', linkedCategory: 'utilities-bills', defaultAmount: 200, color: '#f59e0b' },
  { id: 'env_housing', name: 'Housing & Rent', linkedCategory: 'housing-rent', defaultAmount: 800, color: '#0ea5e9' },
  { id: 'env_transport', name: 'Transportation', linkedCategory: 'transportation', defaultAmount: 150, color: '#8b5cf6' },
  { id: 'env_personal', name: 'Personal Care', linkedCategory: 'personal-care', defaultAmount: 100, color: '#ec4899' },
  { id: 'env_health', name: 'Health & Medical', linkedCategory: 'health-medical', defaultAmount: 150, color: '#ef4444' },
  { id: 'env_education', name: 'Education & Learning', linkedCategory: 'education-learning', defaultAmount: 200, color: '#06b6d4' },
  { id: 'env_subscriptions', name: 'Subscriptions', linkedCategory: 'subscriptions', defaultAmount: 100, color: '#6366f1' },
  { id: 'env_insurance', name: 'Insurance', linkedCategory: 'insurance', defaultAmount: 200, color: '#84cc16' },
  { id: 'env_gifts', name: 'Gifts & Donations', linkedCategory: 'gifts-donations', defaultAmount: 100, color: '#f43f5e' },
  { id: 'env_lifestyle', name: 'Leisure & Lifestyle', linkedCategory: 'leisure-lifestyle', defaultAmount: 200, color: '#a855f7' },
  { id: 'env_other', name: 'Other Expenses', linkedCategory: 'other-expenses', defaultAmount: 100, color: '#6b7280' }
];

// ============================================================================
// FINTASK CATEGORIES (For task management module)
// ============================================================================

export const FINTASK_CATEGORIES = [
  {
    id: 'personal-tasks',
    name: 'Personal',
    icon: User,
    emoji: '👤',
    color: '#ec4899',
    description: 'Personal to-dos',
    subcategories: [
      { id: 'daily-routine', name: 'Daily Routine' },
      { id: 'self-care', name: 'Self-Care' },
      { id: 'appointments', name: 'Appointments' },
      { id: 'errands', name: 'Errands' },
      { id: 'learning-courses', name: 'Learning / Courses' }
    ]
  },
  {
    id: 'work-tasks',
    name: 'Work',
    icon: Briefcase,
    emoji: '💼',
    color: '#3b82f6',
    description: 'Work-related tasks',
    subcategories: [
      { id: 'meetings', name: 'Meetings' },
      { id: 'deadlines', name: 'Deadlines' },
      { id: 'reporting', name: 'Reporting' },
      { id: 'team-projects', name: 'Team Projects' },
      { id: 'emails-communication', name: 'Emails / Communication' },
      { id: 'training', name: 'Training' }
    ]
  },
  {
    id: 'budgeting-tasks',
    name: 'Budgeting',
    icon: Wallet,
    emoji: '🧮',
    color: '#10b981',
    description: 'Budget management tasks',
    subcategories: [
      { id: 'set-budget', name: 'Set Budget' },
      { id: 'review-budget', name: 'Review Budget' },
      { id: 'adjust-budget', name: 'Adjust Budget' },
      { id: 'categorize-expenses', name: 'Categorize Expenses' },
      { id: 'verify-transactions', name: 'Verify Transactions' },
      { id: 'compare-vs-actual', name: 'Compare vs Actual' }
    ]
  },
  {
    id: 'debt-tasks',
    name: 'Debt',
    icon: CreditCard,
    emoji: '💳',
    color: '#dc2626',
    description: 'Debt management tasks',
    subcategories: [
      { id: 'loan-payment', name: 'Loan Payment' },
      { id: 'credit-card-payment', name: 'Credit Card Payment' },
      { id: 'interest-review', name: 'Interest Review' },
      { id: 'overdue-check', name: 'Overdue Check' },
      { id: 'payoff-planning', name: 'Payoff Planning' }
    ]
  },
  {
    id: 'shopping-tasks',
    name: 'Shopping',
    icon: ShoppingCart,
    emoji: '🛒',
    color: '#f59e0b',
    description: 'Shopping-related tasks',
    subcategories: [
      { id: 'grocery-list', name: 'Grocery List' },
      { id: 'household-shopping', name: 'Household Shopping' },
      { id: 'meal-planning', name: 'Meal Planning' },
      { id: 'price-logging', name: 'Price Logging' },
      { id: 'item-bought-dialogue', name: 'Item Bought Dialogue' },
      { id: 'purchase-tracking', name: 'Purchase Tracking' }
    ]
  },
  {
    id: 'investment-tasks',
    name: 'Investment',
    icon: TrendingUp,
    emoji: '📈',
    color: '#0ea5e9',
    description: 'Investment tasks',
    subcategories: [
      { id: 'portfolio-tracking', name: 'Portfolio Tracking' },
      { id: 'risk-assessment', name: 'Risk Assessment' },
      { id: 'research', name: 'Research' },
      { id: 'buy-sell-planning', name: 'Buy/Sell Planning' }
    ]
  },
  {
    id: 'insurance-tasks',
    name: 'Insurance',
    icon: Shield,
    emoji: '🛡️',
    color: '#84cc16',
    description: 'Insurance tasks',
    subcategories: [
      { id: 'policy-renewal-task', name: 'Policy Renewal' },
      { id: 'premium-payment-task', name: 'Premium Payment' },
      { id: 'submit-claim', name: 'Submit Claim' },
      { id: 'claim-follow-up', name: 'Claim Follow-Up' }
    ]
  },
  {
    id: 'tax-tasks',
    name: 'Tax',
    icon: Clock,
    emoji: '📄',
    color: '#78716c',
    description: 'Tax-related tasks',
    subcategories: [
      { id: 'file-tax', name: 'File Tax' },
      { id: 'pay-tax', name: 'Pay Tax' },
      { id: 'upload-tax-documents', name: 'Upload Tax Documents' },
      { id: 'review-deductions', name: 'Review Deductions' },
      { id: 'check-refund-status', name: 'Check Refund Status' }
    ]
  },
  {
    id: 'loan-management-tasks',
    name: 'Loan Management',
    icon: Banknote,
    emoji: '💵',
    color: '#ef4444',
    description: 'Loan management tasks',
    subcategories: [
      { id: 'apply-for-loan', name: 'Apply for Loan' },
      { id: 'review-loan-terms', name: 'Review Loan Terms' },
      { id: 'interest-rate-review', name: 'Interest Rate Review' },
      { id: 'loan-closure', name: 'Loan Closure' }
    ]
  },
  {
    id: 'inventory-tasks',
    name: 'Inventory (Business)',
    icon: Package,
    emoji: '📦',
    color: '#6366f1',
    description: 'Business inventory tasks',
    subcategories: [
      { id: 'stock-review', name: 'Stock Review' },
      { id: 'restocking', name: 'Restocking' },
      { id: 'supplier-tasks', name: 'Supplier Tasks' },
      { id: 'damage-loss-tracking', name: 'Damage / Loss Tracking' },
      { id: 'counting-audit', name: 'Counting / Audit' }
    ]
  },
  {
    id: 'quick-actions',
    name: 'Quick Actions',
    icon: Globe,
    emoji: '⚡',
    color: '#f97316',
    description: 'Quick action tasks',
    subcategories: [
      { id: 'urgent', name: 'Urgent' },
      { id: 'today', name: 'Today' },
      { id: 'five-minute', name: '5-Minute' },
      { id: 'follow-up', name: 'Follow-Up' },
      { id: 'high-priority', name: 'High Priority' }
    ]
  }
];

// ============================================================================
// SHOPPING LIST CATEGORIES (For shopping list module)
// ============================================================================

export const SHOPPING_LIST_CATEGORIES = [
  { id: 'fruits-vegetables', name: 'Fruits & Vegetables', emoji: '🥬', color: '#22c55e' },
  { id: 'dairy-eggs', name: 'Dairy & Eggs', emoji: '🥛', color: '#fbbf24' },
  { id: 'meat-seafood', name: 'Meat & Seafood', emoji: '🥩', color: '#ef4444' },
  { id: 'bakery', name: 'Bakery', emoji: '🍞', color: '#f59e0b' },
  { id: 'pantry-staples', name: 'Pantry Staples', emoji: '🥫', color: '#8b5cf6' },
  { id: 'beverages', name: 'Beverages', emoji: '🥤', color: '#3b82f6' },
  { id: 'snacks-sweets', name: 'Snacks & Sweets', emoji: '🍪', color: '#ec4899' },
  { id: 'frozen-foods', name: 'Frozen Foods', emoji: '🧊', color: '#06b6d4' },
  { id: 'household-items', name: 'Household Items', emoji: '🧹', color: '#64748b' },
  { id: 'personal-care', name: 'Personal Care', emoji: '🧴', color: '#a855f7' },
  { id: 'baby-kids', name: 'Baby & Kids', emoji: '👶', color: '#f472b6' },
  { id: 'pet-supplies', name: 'Pet Supplies', emoji: '🐾', color: '#84cc16' },
  { id: 'other-items', name: 'Other Items', emoji: '📦', color: '#6b7280' }
];

// ============================================================================
// LEGACY ID MAPPING (Backward Compatibility)
// Build a map of all legacy IDs to canonical IDs
// ============================================================================

const buildLegacyIdMap = () => {
  const map = {};
  
  // Map expense categories
  EXPENSE_CATEGORIES.forEach(cat => {
    map[cat.id] = cat.id; // Self-reference
    if (cat.legacyIds) {
      cat.legacyIds.forEach(legacyId => {
        map[legacyId] = cat.id;
        map[legacyId.toLowerCase()] = cat.id;
      });
    }
  });
  
  // Map income categories
  INCOME_CATEGORIES.forEach(cat => {
    map[cat.id] = cat.id;
    if (cat.legacyIds) {
      cat.legacyIds.forEach(legacyId => {
        map[legacyId] = cat.id;
        map[legacyId.toLowerCase()] = cat.id;
      });
    }
  });
  
  // Map savings categories
  SAVINGS_CATEGORIES.forEach(cat => {
    map[cat.id] = cat.id;
    if (cat.legacyIds) {
      cat.legacyIds.forEach(legacyId => {
        map[legacyId] = cat.id;
        map[legacyId.toLowerCase()] = cat.id;
      });
    }
  });
  
  // Map envelope IDs to their linked categories
  BUDGET_ENVELOPES.forEach(env => {
    map[env.id] = env.linkedCategory;
  });
  
  // Map generic/fallback IDs to 'other-expenses'
  // These may appear from legacy data or goal contributions that couldn't derive a category
  map['cat_general'] = 'other-expenses';
  map['general'] = 'other-expenses';
  map['cat_other'] = 'other-expenses';
  map['other'] = 'other-expenses';
  map['uncategorized'] = 'other-expenses';
  
  return map;
};

export const LEGACY_ID_MAP = buildLegacyIdMap();

// ============================================================================
// CATEGORY INDEX (Fast O(1) lookup)
// ============================================================================

const buildCategoryIndex = () => {
  const index = {};
  
  EXPENSE_CATEGORIES.forEach(cat => {
    index[cat.id] = cat;
  });
  
  INCOME_CATEGORIES.forEach(cat => {
    index[cat.id] = cat;
  });
  
  SAVINGS_CATEGORIES.forEach(cat => {
    index[cat.id] = cat;
  });
  
  return index;
};

export const CATEGORY_INDEX = buildCategoryIndex();

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Normalize any category ID to canonical ID
 * @param {string} categoryId - Any category ID (legacy or canonical)
 * @returns {string|null} - Canonical category ID or null if not found
 */
export function normalizeCategoryId(categoryId) {
  if (!categoryId) return null;
  
  const normalized = categoryId.toLowerCase().trim();
  
  // Direct lookup in legacy map
  if (LEGACY_ID_MAP[normalized]) {
    return LEGACY_ID_MAP[normalized];
  }
  
  // Check if already canonical
  if (CATEGORY_INDEX[normalized]) {
    return normalized;
  }
  
  // Try kebab-case conversion
  const kebabCase = normalized.replace(/[_\s]+/g, '-');
  if (LEGACY_ID_MAP[kebabCase]) {
    return LEGACY_ID_MAP[kebabCase];
  }
  
  return null;
}

/**
 * Find category by ID (handles legacy IDs)
 * @param {string} categoryId 
 * @returns {Object|null}
 */
export function findCategoryById(categoryId) {
  if (!categoryId) return null;
  
  // Normalize first
  const canonicalId = normalizeCategoryId(categoryId);
  if (canonicalId && CATEGORY_INDEX[canonicalId]) {
    return CATEGORY_INDEX[canonicalId];
  }
  
  // Direct lookup
  if (CATEGORY_INDEX[categoryId]) {
    return CATEGORY_INDEX[categoryId];
  }
  
  return null;
}

/**
 * Get display name for any category ID
 * @param {string} categoryId 
 * @returns {string}
 */
export function getCategoryDisplayName(categoryId) {
  const category = findCategoryById(categoryId);
  if (category) {
    return category.name;
  }
  
  // Fallback: format the ID nicely
  if (categoryId) {
    return categoryId
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }
  
  return 'Unknown';
}

/**
 * Get category color
 * @param {string} categoryId 
 * @returns {string}
 */
export function getCategoryColor(categoryId) {
  const category = findCategoryById(categoryId);
  return category?.color || '#6b7280';
}

/**
 * Get category icon
 * @param {string} categoryId 
 * @returns {Component|null}
 */
export function getCategoryIcon(categoryId) {
  const category = findCategoryById(categoryId);
  return category?.icon || null;
}

/**
 * Get category emoji
 * @param {string} categoryId 
 * @returns {string}
 */
export function getCategoryEmoji(categoryId) {
  const category = findCategoryById(categoryId);
  return category?.emoji || '📦';
}

/**
 * Get categories by type
 * @param {string} type - 'expense', 'income', or 'savings'
 * @returns {Array}
 */
export function getCategoriesByType(type) {
  switch (type?.toLowerCase()) {
    case CATEGORY_TYPES.EXPENSE:
    case 'expense':
      return EXPENSE_CATEGORIES;
    case CATEGORY_TYPES.INCOME:
    case 'income':
      return INCOME_CATEGORIES;
    case CATEGORY_TYPES.SAVINGS:
    case 'savings':
      return SAVINGS_CATEGORIES;
    default:
      return [];
  }
}

/**
 * FINLIST CONTEXT: Filter expense categories by context
 * 
 * Context behavior:
 * - 'expense': Returns categories available for direct expense entry (excludes shopping-only)
 * - 'shopping': Returns ONLY categories explicitly marked for shopping (groceries-food, personal-care)
 * - null/undefined: Returns all categories (backward compatible)
 * 
 * @param {string} context - 'expense' (direct entry) or 'shopping' (shopping list flow)
 * @returns {Array} - Filtered expense categories
 */
export function getExpenseCategoriesForContext(context) {
  if (!context) return EXPENSE_CATEGORIES;
  
  if (context === 'shopping') {
    // FINLIST CONTEXT: Shopping List shows ONLY groceries-food and personal-care
    // These are the two canonical categories for shopping list items
    return EXPENSE_CATEGORIES.filter(category => 
      category.availableIn && category.availableIn.includes('shopping')
    );
  }
  
  // For 'expense' context: exclude categories that are shopping-only
  return EXPENSE_CATEGORIES.filter(category => {
    // If no availableIn defined, category is available everywhere (backward compatible)
    if (!category.availableIn) return true;
    // Otherwise, check if context is in the availableIn array
    return category.availableIn.includes(context);
  });
}

/**
 * Get subcategories for a category
 * @param {string} categoryId 
 * @returns {Array}
 */
export function getSubcategories(categoryId) {
  const category = findCategoryById(categoryId);
  return category?.subcategories || [];
}

/**
 * Check if category ID is canonical
 * @param {string} categoryId 
 * @returns {boolean}
 */
export function isCanonicalCategoryId(categoryId) {
  return !!CATEGORY_INDEX[categoryId];
}

/**
 * Get budget envelope for category
 * @param {string} categoryId 
 * @returns {Object|null}
 */
export function getBudgetEnvelope(categoryId) {
  const category = findCategoryById(categoryId);
  if (category?.budgetEnvelope) {
    return BUDGET_ENVELOPES.find(env => env.id === category.budgetEnvelope);
  }
  return null;
}

/**
 * Get all canonical expense category IDs
 * @returns {Array<string>}
 */
export function getExpenseCategoryIds() {
  return EXPENSE_CATEGORIES.map(c => c.id);
}

/**
 * Get all canonical income category IDs
 * @returns {Array<string>}
 */
export function getIncomeCategoryIds() {
  return INCOME_CATEGORIES.map(c => c.id);
}

/**
 * Get all canonical savings category IDs
 * @returns {Array<string>}
 */
export function getSavingsCategoryIds() {
  return SAVINGS_CATEGORIES.map(c => c.id);
}

/**
 * Check if a category ID is a savings category
 * This includes savings categories AND debt-related budget envelopes
 * (since paying off debt is effectively a form of savings/wealth building)
 * @param {string} categoryId - The category ID to check
 * @returns {boolean}
 */
export function isSavingsCategory(categoryId) {
  if (!categoryId) return false;
  
  const normalizedId = categoryId.toLowerCase().trim();
  
  // Check if it's in the savings categories
  const savingsIds = getSavingsCategoryIds();
  if (savingsIds.includes(normalizedId)) return true;
  
  // Check CATEGORY_INDEX for type: SAVINGS
  const category = CATEGORY_INDEX[normalizedId];
  if (category?.type === CATEGORY_TYPES.SAVINGS) return true;
  
  // Budget template savings envelopes (debt-repayment is treated as savings allocation)
  const templateSavingsIds = ['emergency-fund', 'debt-repayment', 'retirement-savings', 'home-savings'];
  if (templateSavingsIds.includes(normalizedId)) return true;
  
  // Check for debt-related keywords (treated as savings in envelope budgeting)
  if (normalizedId.includes('debt') || normalizedId.includes('repayment')) return true;
  
  return false;
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  CATEGORY_TYPES,
  EXPENSE_CATEGORIES,
  INCOME_CATEGORIES,
  SAVINGS_CATEGORIES,
  BUDGET_ENVELOPES,
  SHOPPING_LIST_CATEGORIES,
  FINTASK_CATEGORIES,
  LEGACY_ID_MAP,
  CATEGORY_INDEX,
  // Helper functions
  normalizeCategoryId,
  findCategoryById,
  getCategoryDisplayName,
  getCategoryColor,
  getCategoryIcon,
  getCategoryEmoji,
  getCategoriesByType,
  getExpenseCategoriesForContext, // FINLIST CONTEXT: Filter by context
  getSubcategories,
  isCanonicalCategoryId,
  getBudgetEnvelope,
  getExpenseCategoryIds,
  getIncomeCategoryIds,
  getSavingsCategoryIds,
  isSavingsCategory
};
